import React, { useState } from "react"
import { Configure } from "react-instantsearch"
import { ReactSortable } from "react-sortablejs"
import { useMutation } from "urql"

import { Button } from "../components/shared/Buttons"
import HealMeTooltip from "../components/shared/HealMeTooltip"
import { Box, ProfilePageEmptyStateBox } from "../components/shared/Layout"
import { UnderlinedLink } from "../components/shared/Links"
import { EndorsementPill } from "../components/shared/Pill"
import { AlgoliaSearchProvider } from "../components/shared/SearchInputs"
import Typography from "../components/shared/Typography"
import { keywordLimitReached } from "../constants/keywordLimits"
import { useProfileStatus } from "../contexts/ProfileStatusContext"
import { useToast } from "../contexts/ToastContext"

import { EndorsementsFlyout } from "./ProfileEdit/EndorsementsFlyout"
import ProfileEditProvider from "./ProfileEdit/ProfileEditProvider"
import ProfileEditView from "./ProfileEdit/ProfileEditView"

const DELETE_SKILLS = `
  mutation DeleteSkills($skillId: String!) {
    deleteSkills(skillId: $skillId) {
      result
      skills {
        id
        endorsementsCount
        specialty { id name }
      }
      errors
    }
  }
`

const MOVE_SKILLS = `
  mutation MoveSkills($skillArray: [String!]!) {
    moveSkills(skillArray: $skillArray) {
      result
      skills {
        id
        endorsementsCount
        specialty { id name }
      }
      errors
    }
  }
`

const Endorsements = ({ path, skills, specialtiesByLetter, usingBuilderV2, maxKeywords = null }) => {
  const { profileStatusData } = useProfileStatus()
  const [skillsState, setSkillsState] = useState(skills)
  const [flyoutVisible, setFlyoutVisible] = useState(false)

  const [, deleteSkills] = useMutation(DELETE_SKILLS)
  const [, moveSkills] = useMutation(MOVE_SKILLS)

  const { showToast } = useToast()

  const atKeywordLimit = keywordLimitReached(skillsState.length, maxKeywords)

  const removeSkill = (skill) => {
    deleteSkills({ skillId: skill.id.toString() }).then((result) => {
      if (result?.data?.deleteSkills?.result === "success") {
        setSkillsState(result.data.deleteSkills.skills)
        setFlyoutVisible(false)
        showToast("Successfully deleted endorsement topic")
      } else {
        showToast({ type: "error", content: "Failed to delete endorsement topic" })
        console.error(result) // eslint-disable-line no-console
      }
    })

    const index = skillsState.indexOf(skill)
    if (index !== -1) {
      const copySkills = [...skillsState]
      copySkills.splice(index, 1)
      setSkillsState(copySkills)
    }
  }

  return (
    <ProfileEditView path={path} profileStatusData={profileStatusData}>
      {skillsState.length === 0 ? (
        <ProfilePageEmptyStateBox title="Endorsements" onButtonClick={() => setFlyoutVisible(true)}>
          Why do your clients come to see you? List these here.{" "}
          <UnderlinedLink
            href="https://help.heal.me/en/articles/8441399-what-are-endorsements-and-which-should-i-pick"
            target="_blank">
            Learn more
          </UnderlinedLink>
        </ProfilePageEmptyStateBox>
      ) : (
        <>
          <Box data-test-id="endorsements">
            <div className="flex items-center justify-between border-b border-gray-light pb-6 sm:border-none sm:pb-0">
              <Typography variant="capitalHeading">Endorsements</Typography>
              <div data-tooltip-id="disabled-tooltip">
                <Button floatingInMobile={true} disabled={atKeywordLimit} onClick={() => setFlyoutVisible(true)}>
                  Add new
                </Button>
              </div>
              {atKeywordLimit && (
                <HealMeTooltip
                  id="disabled-tooltip"
                  place="bottom"
                  content={`Note: You can add a maximum of ${maxKeywords} keywords.`}
                />
              )}
            </div>
            <Typography variant="subtitle" className="mt-6 sm:mt-2">
              {usingBuilderV2
                ? "Drag to re-order. Keywords are displayed in 3 columns on your website (preview below)."
                : "Drag to re-order each endorsement."}
            </Typography>
            <ReactSortable
              className="mt-6 flex flex-wrap gap-2 sm:mt-4"
              list={skillsState}
              setList={(newSkills) => {
                if (JSON.stringify(newSkills) !== JSON.stringify(skillsState)) {
                  const newOrder = newSkills.map((skill, i) => JSON.stringify({ id: skill.id, position: i + 1 }))
                  setSkillsState(newSkills)
                  moveSkills({ skillArray: newOrder }).then((result) => {
                    if (result?.data?.moveSkills?.result !== "success") {
                      console.error(result) // eslint-disable-line no-console
                      showToast({
                        type: "error",
                        content:
                          "There was error reordering your endorsement topics. Please try again later or contact support if the error persists."
                      })
                    }
                  })
                }
              }}>
              {skillsState.map((skill) => (
                <EndorsementPill key={skill.id} draggable active onCloseButtonClick={() => removeSkill(skill)}>
                  {skill.specialty.name}
                </EndorsementPill>
              ))}
            </ReactSortable>

            {/* V2 Website Preview */}
            {usingBuilderV2 && skillsState.length > 0 && (
              <div className="mt-8 rounded-lg border border-gray-light bg-gray-ultralight p-6">
                <Typography variant="body" className="mb-4 font-bold text-gray-dark">
                  Website Preview (3-column layout):
                </Typography>
                <div className="flex gap-8">
                  {(() => {
                    const columnCount = 3
                    const chunkSize = Math.ceil(skillsState.length / columnCount)
                    const columns = []
                    for (let i = 0; i < columnCount; i++) {
                      columns.push(skillsState.slice(i * chunkSize, (i + 1) * chunkSize))
                    }
                    return columns.map((columnSkills, colIndex) => (
                      <div key={colIndex} className="flex flex-1 flex-col gap-2">
                        {columnSkills.map((skill) => (
                          <div key={skill.id} className="flex items-start gap-2">
                            {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
                            <span className="text-primary mt-1">✓</span>
                            <span className="text-sm text-gray-dark">{skill.specialty.name}</span>
                          </div>
                        ))}
                      </div>
                    ))
                  })()}
                </div>
              </div>
            )}
          </Box>
        </>
      )}
      <EndorsementsFlyout
        specialtiesByLetter={specialtiesByLetter}
        skills={skillsState}
        setSkills={setSkillsState}
        visible={flyoutVisible}
        closeFlyout={() => setFlyoutVisible(false)}
        maxKeywords={maxKeywords}
      />
    </ProfileEditView>
  )
}

export default function ProfileEditEndorsements({ profileStatusData, ...props }) {
  return (
    <ProfileEditProvider profileStatusData={profileStatusData}>
      <AlgoliaSearchProvider indexName="Specialty">
        <Configure hitsPerPage={50} filters="NOT ordered_kind:1" />
        <Endorsements {...props} />
      </AlgoliaSearchProvider>
    </ProfileEditProvider>
  )
}
